import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  Target,
  ListTodo,
  Plus,
  Wallet,
  MoreHorizontal,
  FolderPlus,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface MobileNavItem {
  id: string
  label: string
  icon: LucideIcon
  badge?: number
  /** Renders as a nested shortcut (used for pinned lists under "Lists") */
  indent?: boolean
}

interface MobileNavProps {
  currentView: string
  isTasksView: boolean
  isMoreView: boolean
  inboxCount: number
  tasksItems: MobileNavItem[]
  moreItems: MobileNavItem[]
  onNavigate: (view: string) => void
  onNewTask: () => void
  onNewProject: () => void
}

// Bottom tab bar shown only on small screens (md:hidden). Desktop keeps the
// header nav untouched — both drive the same currentView state in Dashboard.
export function MobileNav({
  currentView,
  isTasksView,
  isMoreView,
  inboxCount,
  tasksItems,
  moreItems,
  onNavigate,
  onNewTask,
  onNewProject,
}: MobileNavProps) {
  const [openSheet, setOpenSheet] = useState<'tasks' | 'more' | null>(null)

  const navigate = (view: string) => {
    onNavigate(view)
    setOpenSheet(null)
  }

  const tabClass = (active: boolean) =>
    cn(
      'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors',
      active ? 'text-primary' : 'text-muted-foreground'
    )

  const sheetItems = openSheet === 'tasks' ? tasksItems : moreItems

  return (
    <div className="md:hidden">
      {openSheet && (
        <div
          className="backdrop-in fixed inset-0 z-30 bg-black/50"
          onClick={() => setOpenSheet(null)}
        />
      )}

      {openSheet && (
        <div className="sheet-in fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-40 max-h-[60vh] overflow-y-auto rounded-t-xl border bg-background shadow-2xl">
          <div className="py-2">
            {sheetItems.map((item) => {
              const isActive =
                currentView === item.id ||
                (item.id === 'projects' && currentView === 'project') ||
                (item.id === 'habits' && currentView === 'habit') ||
                (item.id === 'lists' && currentView === 'list')
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.id)}
                  className={cn(
                    'flex w-full items-center gap-3 py-3 text-left text-sm',
                    item.indent ? 'pl-10 pr-4' : 'px-4',
                    isActive ? 'bg-accent text-foreground' : 'text-foreground'
                  )}
                >
                  <item.icon className={cn(
                    'text-muted-foreground',
                    item.indent ? 'h-4 w-4' : 'h-5 w-5'
                  )} />
                  <span className={cn('flex-1 truncate', item.indent && 'text-muted-foreground')}>
                    {item.label}
                  </span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </button>
              )
            })}

            {openSheet === 'tasks' && (
              <>
                <div className="my-1 border-t" />
                <button
                  onClick={() => {
                    onNewProject()
                    setOpenSheet(null)
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-foreground"
                >
                  <FolderPlus className="h-5 w-5 text-muted-foreground" />
                  New Project
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <nav className="gpu-fixed fixed inset-x-0 bottom-0 z-40 border-t bg-card pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch">
          <button
            onClick={() => navigate('today')}
            className={tabClass(currentView === 'today' && !openSheet)}
          >
            <Target className="h-5 w-5" />
            Today
          </button>

          <button
            onClick={() => setOpenSheet(openSheet === 'tasks' ? null : 'tasks')}
            className={tabClass(openSheet === 'tasks' || (isTasksView && !openSheet))}
          >
            <span className="relative">
              <ListTodo className="h-5 w-5" />
              {inboxCount > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-white">
                  {inboxCount}
                </span>
              )}
            </span>
            Tasks
          </button>

          <button
            onClick={() => {
              setOpenSheet(null)
              onNewTask()
            }}
            className="flex flex-1 items-center justify-center"
            aria-label="New Task"
          >
            <span className="-mt-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
              <Plus className="h-6 w-6" />
            </span>
          </button>

          <button
            onClick={() => navigate('finance')}
            className={tabClass(currentView === 'finance' && !openSheet)}
          >
            <Wallet className="h-5 w-5" />
            Finance
          </button>

          <button
            onClick={() => setOpenSheet(openSheet === 'more' ? null : 'more')}
            className={tabClass(openSheet === 'more' || (isMoreView && !openSheet))}
          >
            <MoreHorizontal className="h-5 w-5" />
            More
          </button>
        </div>
      </nav>
    </div>
  )
}
